import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface BlackforestlabsFluxKontextProParams {
  /**
   * 文本提示词，描述要生成的图像内容
   */
  prompt: string;
  /**
   * 输入图像，Base64编码或URL
   */
  input_image?: string;
  /**
   * 输入图像2，Base64编码或URL
   */
  input_image_2?: string;
  /**
   * 输入图像3，Base64编码或URL
   */
  input_image_3?: string;
  /**
   * 输入图像4，Base64编码或URL
   */
  input_image_4?: string;
  /**
   * 随机种子，用于可重现的生成结果
   */
  seed?: number;
  /**
   * 图像宽高比，格式如 "16:9"，范围为 "21:9" 到 "9:21"
   * @default "1:1"
   */
  aspect_ratio?: string;
  /**
   * 输出格式
   * @default "jpeg"
   * @values jpeg | png
   */
  output_format?: 'jpeg' | 'png';
  /**
   * 回调URL，必须以http://或https://开头，长度1-2083字符
   */
  webhook_url?: string;
  /**
   * 回调密钥
   */
  webhook_secret?: string;
  /**
   * 是否启用提示词增强
   * @default false
   */
  prompt_upsampling?: boolean;
  /**
   * 安全容忍度，范围0-6
   * @default 2
   * @range 0 - 6
   */
  safety_tolerance?: number;
}

export interface BlackforestlabsFluxKontextProResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * blackforestlabs_flux_kontext_pro
 *
 * Black Forest Labs Flux Kontext Pro 图像生成接口
 * 
 * Model: flux-kontext-pro (固定模型)
 * Features: 文本生成图像、多图输入编辑功能
 * Input Images: 支持1-4张输入图片（Base64或URL）
 * Aspect Ratio: 支持21:9到9:21范围的宽高比（默认1:1）
 * Output Format: jpeg、png（默认jpeg）
 * Advanced: 支持提示词增强、安全容忍度控制（0-6）、回调通知
 * 
 * API Documentation: https://docs.bfl.ai/api-reference/tasks/edit-or-create-an-image-with-flux-kontext-pro
 *
 * @example
 * ```typescript
 * import { initSeacloud, blackforestlabsFluxKontextPro } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await blackforestlabsFluxKontextPro({
 *   // 参数见 BlackforestlabsFluxKontextProParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 BlackforestlabsFluxKontextProParams 接口定义
 * @returns 生成的资源列表
 */
export async function blackforestlabsFluxKontextPro(
  params: BlackforestlabsFluxKontextProParams,
): Promise<BlackforestlabsFluxKontextProResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'blackforestlabs_flux_kontext_pro',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: BlackforestlabsFluxKontextProResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
